import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useVideoPlayerStore = defineStore('videoPlayer', () => {
    // Current active player instance
    const activePlayer = ref(null);
    
    // Global mute state (in-memory only, not persisted)
    // Always start muted for autoplay compliance
    const globalMuteState = ref(true);
    
    // Track if user has interacted with mute during this page visit
    const userHasInteracted = ref(false);

    // Player state
    const playerState = ref({
        playing: false,
        paused: true,
        currentTime: 0,
        duration: 0,
        loading: false,
        ended: false,
        muted: globalMuteState.value,
    });

    // Ad state
    const adState = ref({
        playing: false,
        duration: 0,
        currentTime: 0,
        canSkip: false,
    });

    // Network performance metrics (learned during playback)
    const networkPerformance = ref({
        connectionSpeed: null,        // Mbps - detected once
        measuredBandwidth: null,      // Real bandwidth from HLS.js
        lastBandwidthUpdate: null,    // When we last measured
    });

    // Quality configuration (progressive improvement)
    const qualityConfig = ref({
        highestStableLevel: -1,       // Highest quality that played without buffering
        preferredStartLevel: undefined, // Where to start playbook
        minAcceptableLevel: -1,       // Never go below this proven level
    });

    // Device capabilities (detected once)
    const deviceCapabilities = ref({
        supportsVP9: null,            // VP9 codec support
        deviceType: null,             // mobile/tablet/desktop
        hasGoodConnection: null,      // Initial connection quality
    });

    // HLS configuration computed from above
    const hlsConfig = ref({
        abrEstimate: null,            // Adaptive bitrate starting estimate
        bufferSize: null,             // Calculated buffer size
    });

    // Computed properties
    const isMuted = computed(() => playerState.value.muted);
    const isPlaying = computed(() => playerState.value.playing);
    const isAdPlaying = computed(() => adState.value.playing);
    const progress = computed(() => {
        if (playerState.value.duration === 0) return 0;
        return (playerState.value.currentTime / playerState.value.duration) * 100;
    });
    const adProgress = computed(() => {
        if (adState.value.duration === 0) return 0;
        return (adState.value.currentTime / adState.value.duration) * 100;
    });
    const adRemainingTime = computed(() => {
        const remaining = Math.max(0, adState.value.duration - adState.value.currentTime);
        const minutes = Math.floor(remaining / 60);
        const seconds = Math.floor(remaining % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    });

    // Actions
    const setActivePlayer = (player) => {
        activePlayer.value = player;
    };

    const updatePlayerState = (updates) => {
        Object.assign(playerState.value, updates);
        
        // Sync mute state if changed
        if ('muted' in updates) {
            setGlobalMuteState(updates.muted);
        }
    };

    const updateAdState = (updates) => {
        Object.assign(adState.value, updates);
    };

    const setGlobalMuteState = (muted) => {
        globalMuteState.value = muted;
        userHasInteracted.value = true;
        playerState.value.muted = muted;
    };

    const getInitialMuteState = () => {
        // Always return current state (starts muted, changes only during session)
        return globalMuteState.value;
    };

    const resetPlayerState = () => {
        playerState.value = {
            playing: false,
            paused: true,
            currentTime: 0,
            duration: 0,
            loading: false,
            ended: false,
            muted: globalMuteState.value,
        };
    };

    const resetAdState = () => {
        adState.value = {
            playing: false,
            duration: 0,
            currentTime: 0,
            canSkip: false,
        };
    };

    // Initialize network performance (called once)
    const initializeNetworkPerformance = () => {
        if (!networkPerformance.value.connectionSpeed) {
            networkPerformance.value.connectionSpeed = navigator.connection?.downlink || 10;
            networkPerformance.value.lastBandwidthUpdate = Date.now();
        }
        return networkPerformance.value.connectionSpeed;
    };

    // Update with real measured bandwidth from HLS
    const updateMeasuredBandwidth = (bandwidth) => {
        if (bandwidth > (networkPerformance.value.measuredBandwidth || 0)) {
            networkPerformance.value.measuredBandwidth = bandwidth;
            networkPerformance.value.lastBandwidthUpdate = Date.now();
            // Update ABR estimate based on real measurements
            hlsConfig.value.abrEstimate = Math.min(bandwidth * 0.9, 5000000);
        }
    };

    // Record successful quality playbook
    const recordStableQualityLevel = (level) => {
        if (level > qualityConfig.value.highestStableLevel) {
            qualityConfig.value.highestStableLevel = level;
            qualityConfig.value.preferredStartLevel = level; // Start here next time
            qualityConfig.value.minAcceptableLevel = Math.max(
                qualityConfig.value.minAcceptableLevel,
                level - 1 // Allow one level down but no more
            );
        }
    };

    // Get optimal ABR estimate for HLS configuration
    const getOptimalAbrEstimate = () => {
        // Use measured bandwidth if available
        if (networkPerformance.value.measuredBandwidth) {
            return hlsConfig.value.abrEstimate;
        }
        
        // Otherwise calculate from connection speed (once)
        if (!hlsConfig.value.abrEstimate) {
            const speed = initializeNetworkPerformance();
            if (speed >= 10) {
                hlsConfig.value.abrEstimate = 5000000; // 5 Mbps
            } else if (speed >= 5) {
                hlsConfig.value.abrEstimate = 2000000; // 2 Mbps
            } else if (speed >= 2) {
                hlsConfig.value.abrEstimate = 1000000; // 1 Mbps
            } else {
                hlsConfig.value.abrEstimate = 500000;  // 500 kbps
            }
        }
        return hlsConfig.value.abrEstimate;
    };

    // Get preferred starting quality level
    const getPreferredStartLevel = () => {
        return qualityConfig.value.preferredStartLevel;
    };

    // Initialize device capabilities (once)
    const initializeDeviceCapabilities = () => {
        if (!deviceCapabilities.value.deviceType) {
            // Basic device detection
            const userAgent = navigator.userAgent;
            if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
                deviceCapabilities.value.deviceType = 'mobile';
            } else if (/iPad/i.test(userAgent)) {
                deviceCapabilities.value.deviceType = 'tablet';
            } else {
                deviceCapabilities.value.deviceType = 'desktop';
            }
            
            // VP9 support detection
            const video = document.createElement('video');
            deviceCapabilities.value.supportsVP9 = video.canPlayType('video/webm; codecs="vp9"') !== '';
            
            // Connection quality
            deviceCapabilities.value.hasGoodConnection = 
                initializeNetworkPerformance() >= 5;
        }
        return deviceCapabilities.value;
    };

    // Get performance summary for debugging
    const getPerformanceSummary = () => {
        return {
            network: networkPerformance.value,
            quality: qualityConfig.value,
            device: deviceCapabilities.value,
            hls: hlsConfig.value,
        };
    };

    return {
        // State
        activePlayer,
        playerState,
        adState,
        globalMuteState,
        userHasInteracted,
        networkPerformance,
        qualityConfig,
        deviceCapabilities,
        hlsConfig,
        
        // Computed
        isMuted,
        isPlaying,
        isAdPlaying,
        progress,
        adProgress,
        adRemainingTime,
        
        // Actions
        setActivePlayer,
        updatePlayerState,
        updateAdState,
        setGlobalMuteState,
        getInitialMuteState,
        resetPlayerState,
        resetAdState,
        
        // Performance & Configuration
        initializeNetworkPerformance,
        updateMeasuredBandwidth,
        recordStableQualityLevel,
        getOptimalAbrEstimate,
        getPreferredStartLevel,
        initializeDeviceCapabilities,
        getPerformanceSummary,
    };
});